import { EditorState } from '@codemirror/state';
import { EditorView, minimalSetup } from 'codemirror';
import { generateSignature, getCurrentAlgorithm, getCurrentSecret, getValidationSubject, validateSignature } from './algorithms';
import * as helper from './helper';

// TODO: additional refactoring at the end
// TODO: maybe move editor into a seperate js file, so that the initalization takes place there, and we only import the editor here
// CodeMirror Editor Setup
const theme = EditorView.theme({
	"&": {
		// boxShadow: '5px 5px 20px 5px #00000078, 0 1px 2px #0000003d'
		border: '1px solid #0000002d',
		borderRadius: '.375rem'
	},
	".cm-content": {
		padding: '2em'
	}
})

const state = EditorState.create({
	extensions: [
		theme,
		minimalSetup,
		// EditorView.editorAttributes.of({ class: ""}),
		EditorView.updateListener.of((update) => {
			if (update.docChanged && validateJWT(update.state.doc.toString())) {
				handleJWT(update.state.doc.toString());
			}
		}),
		EditorView.lineWrapping,
	]
});

const editor = new EditorView({
	state,
	parent: document.querySelector('#wtJwtCodemirror'),
});

/**
 * prepareTables
 * @description prepares the provided table, fills data from the JWT etc.
 * @param {string} target 
 * @param {string} data
 */
const prepareTable = (part, target, data) => {
	const table = document.querySelector(`${target}`);
	table.tBodies[0].innerHTML = "";
	const payloadInfo = JSON.parse(data);
	Object.keys(payloadInfo).forEach(key => {
		insertJwtRow(table, part, key, payloadInfo[key]);
	});
	document.querySelector(`${target} thead.visually-hidden`)?.classList?.remove('visually-hidden');
}

/**
 * insertJwtRow
 * @param {Element} table the srcElement of the table, on which the row shall be inserted
 * @param {string} part if the row shall be part of the header of body Table
 * @param {string} key data for the key input field
 * @param {string} value data for the value input field
 */
const insertJwtRow = (table, part, key = '', value = '') => {
	const row = table.tBodies[0].insertRow(table.tBodies[0].rows.length);
	
	const keyColumn = document.createElement('input');
	keyColumn.id = `${part}Key${table.tBodies[0].rows.length}`;
	keyColumn.value = key;
	keyColumn.className = 'form-control';
	keyColumn.oninput = (e) => editJWT(e, keyColumn);
	row.insertCell().appendChild(keyColumn);
	
	const valueColumn = document.createElement('input');
	valueColumn.id = `${part}Value${table.tBodies[0].rows.length}`;
	valueColumn.value = value;
	valueColumn.className = 'form-control';
	valueColumn.oninput = (e) => editJWT(e, valueColumn);
	row.insertCell().appendChild(valueColumn);
	
	row.id = `${part}Row${table.tBodies[0].rows.length}`;

	const deleteColumn = document.createElement('button');
	deleteColumn.id = `${part}Delete${table.tBodies[0].rows.length}`;
	deleteColumn.className = 'btn btn-outline-danger';
	deleteColumn.innerHTML = `<i class="fa fa-trash"></i>`
	deleteColumn.onclick = () => document.getElementById(`${part}Row${table.tBodies[0].rows.length}`).remove();
	row.insertCell().appendChild(deleteColumn);
}

/**
 * editJWT
 * @description is called when the value of the fields is changed, or the secret is touched.
 * @param {Event} event the event
 * @param {Element} input srcElement of the input
 */
const editJWT = async (event, input) => {
	// Simple solution, going over all fields that are part of "header/payload" and get their values
	const newHeader = {};
	const headerInputs = document.querySelectorAll('#jwtHeaderTable input');
	for (let i = 0; i < Math.round(headerInputs.length / 2); i++) {
		const key = headerInputs[i + i].value;
		const value = headerInputs[i * 2 + 1].value;
		newHeader[key] = value;
	}
	const newEncodedHeader = helper.removePadding(window.btoa(JSON.stringify(newHeader)));

	const newPayload = {};
	const payloadInputs = document.querySelectorAll('#jwtPayloadTable input');
	for (let i = 0; i < Math.round(payloadInputs.length / 2); i++) {
		const key = payloadInputs[i + i].value;
		const value = payloadInputs[i * 2 + 1].value;
		newPayload[key] = value;
	}
	const newEncodedPayload = helper.removePadding(window.btoa(JSON.stringify(newPayload)));
	const buildJwt = `${newEncodedHeader}.${newEncodedPayload}`;
	const signature = await generateSignature(getCurrentAlgorithm(), getCurrentSecret(), buildJwt);
	const newJWT = `${buildJwt}.${signature}`;

	/**
	 * Hack workaround ahead :'D
	 * store current position of input field, and set it again after focus was re-established
	 * Find a better way for this... :'D
	*/
	editor.dispatch({
		changes: {from: 0, to: editor.state.doc.length, insert: newJWT}
	});
	const elem = document.getElementById(input.id);
	const currentCursorPosition = event.target.selectionStart;
	elem.focus();
	elem.setSelectionRange(currentCursorPosition, currentCursorPosition);

	validateJWTSignature();
}

/**
 * handleJwt
 * @param {string} input the currently entered JWT
 */
const handleJWT = (input) => {
	const jwtToken = input.split('.');
	
	jwtToken[0] = window.atob(helper.addPadding(jwtToken[0]));
	jwtToken[1] = window.atob(helper.addPadding(jwtToken[1]));

	prepareTable('header', '#jwtHeaderTable', jwtToken[0]);
	prepareTable('payload', '#jwtPayloadTable', jwtToken[1]);
	validateJWTSignature();
};

/**
 * copyJWT
 * @description copy the content of the provided table in JSON format
 * @param {string} part header or body
 */
const copyJwt = (part) => {
	const value = window.atob(editor.state.doc.toString().split('.')[part]);
	navigator.clipboard.writeText(value);
}

/**
 * validateJWT
 * @description validates the provided JWT token
 * @param {string} input 
 * @returns {boolean}
 */
const validateJWT = (input) => {
	// TODO: improve validation
	return input.length != 0 && input.split('.').length === 3;
}

// Checks the signature, and handles valid/invalid indication
const validateJWTSignature = async () => {
	console.log('test');
	const result = await validateSignature(getCurrentAlgorithm(), getValidationSubject(), editor.state.doc.toString());
	const elem = document.getElementById('jwtSignatureStatus');
	if (result) {
		elem.classList.add('alert-success');
		elem.classList.remove('alert-danger');
		elem.innerHTML = '<i class="fa-solid fa-check"></i> Signature valid';
	} else {
		elem.classList.add('alert-danger');
		elem.classList.remove('alert-success');
		elem.innerHTML = '<i class="fa-solid fa-xmark"></i> Signature invalid';
	}
};

// handles the algorithm change, enables/disables privateKey/publicKey fields
const handleAlgorithmChange = (e) => {
	if (e.target.value.includes('RS')) {
		document.getElementById('jwtSecret').setAttribute('hidden', true);
		document.getElementById('jwtPrivateKey').removeAttribute('hidden');
		document.getElementById('jwtPublicKey').removeAttribute('hidden');
		document.querySelector('label[for="jwtSecret"]').setAttribute('hidden', true);
		document.querySelector('label[for="jwtPrivateKey"]').removeAttribute('hidden');
		document.querySelector('label[for="jwtPublicKey"]').removeAttribute('hidden');
	} else {
		document.getElementById('jwtSecret').removeAttribute('hidden');
		document.getElementById('jwtPrivateKey').setAttribute('hidden', true);
		document.getElementById('jwtPublicKey').setAttribute('hidden', true);
		document.querySelector('label[for="jwtSecret"]').removeAttribute('hidden');
		document.querySelector('label[for="jwtPrivateKey"]').setAttribute('hidden', true);
		document.querySelector('label[for="jwtPublicKey"]').setAttribute('hidden', true);
	}

	document.querySelectorAll('input')
		.forEach(input => {
			if (input.value === 'alg') {
				const id = input.id.substring(input.id.length - 1, input.id.length);
				document.getElementById(`headerValue${id}`).value = e.target.value;
				editJWT(e, document.getElementById('jwtSecret'));
			}
		});
}

// initialize JWT Token
const header = helper.removePadding(window.btoa(JSON.stringify({
	"alg": "HS256",
	"typ": "JWT"
})));

const payload = helper.removePadding(window.btoa(JSON.stringify({
	"sub": "1234",
	"name": "web-tools.app",
	"iat": Date.now()
})));

generateSignature(getCurrentAlgorithm(), getCurrentSecret(), `${header}.${payload}`)
	.then((signature) => {
		editor.dispatch({
			changes: {from: 0, to: editor.state.doc.length, insert: `${header}.${payload}.${signature}` }
		})
	});

// Listener Registration
document.getElementById('jwtHeaderCopy').onclick = () => copyJwt(0);
document.getElementById('jwtPayloadCopy').onclick = () => copyJwt(1);
document.getElementById('jwtAddHeaderRow').onclick = () => insertJwtRow(document.getElementById('jwtHeaderTable'), 'header');
document.getElementById('jwtAddPayloadRow').onclick = () => insertJwtRow(document.getElementById('jwtPayloadTable'), 'payload');
document.getElementById('jwtAlgorithm').onchange = (e) => handleAlgorithmChange(e);
document.getElementById('jwtSecret').oninput = (e) => editJWT(e, document.getElementById('jwtSecret'));
document.getElementById('jwtSecret').value = "web-tools.app";
document.getElementById('jwtPublicKey').oninput = (e) => validateJWTSignature();
document.getElementById('jwtPrivateKey').oninput = (e) => editJWT(e, document.getElementById('jwtPrivateKey'));